import { useMemo } from "react";
import { useConfig } from "./useConfig";
export {
  getDispatchStatusColorKeys,
  resolveDispatchStatusColor,
} from "../shared/Scheduler/dispatchColors";

/**
 * Hook to provide dispatch color configuration with intelligent fallbacks
 * @returns {Object} Color configuration object with status colors and helper functions
 */
export const useDispatchColors = () => {
  const { configs } = useConfig();

  const colorConfig = useMemo(() => {
    const dispatchConfig = configs?.features?.Dispatch;
    const statusColors = dispatchConfig?.statusColors || {};
    const selectedTheme = dispatchConfig?.themes?.default || "standard";
    const colorThemes = dispatchConfig?.colorThemes || {};

    // Default fallback colors when no dispatch config is available
    const defaultStatusColors = {
      // Trucking statuses
      new: "#38bc8a", // gray
      pending: "#f59e0b", // orange
      enRoute: "#3b82f6", // blue
      delivered: "#06b6d4", // cyan
      invoiced: "#8b5cf6", // purple
      // Service/Repair statuses
      booked: "#3b82f6", // blue
      assigned: "#eab308", // yellow
      inProgress: "#06b6d4", // cyan
      completed: "#10b981", // green
    };

    // Merge configured colors with defaults (configured colors take precedence)
    const finalStatusColors = {
      ...defaultStatusColors,
      ...statusColors,
    };

    // If a theme is selected (other than standard), use theme colors
    if (selectedTheme !== "standard" && colorThemes[selectedTheme]) {
      return {
        statusColors: {
          ...finalStatusColors,
          ...colorThemes[selectedTheme].statusColors,
        },
        selectedTheme,
      };
    }

    // Default to configured status colors with fallbacks
    return {
      statusColors: finalStatusColors,
      selectedTheme,
    };
  }, [configs?.features?.Dispatch]);

  const workHours = useMemo(() => {
    const dispatchConfig = configs?.features?.Dispatch;
    const timeline = dispatchConfig?.timeline;

    return timeline?.workHours || { start: 8, end: 18 };
  }, [configs?.features?.Dispatch]);

  /**
   * Get color for a job/event based on its status
   * @param {Object} job - Job object with status property
   * @returns {string} Hex color code
   */
  const getJobColor = (job) => {
    const { status } = job;

    // Status-based coloring
    if (status && colorConfig.statusColors[status]) {
      return colorConfig.statusColors[status];
    }

    // Map job stage titles to IDs (from job-stages.tsx)
    const statusTitleToIdMappings = {
      // Title -> ID mappings
      New: "new",
      Pending: "pending",
      "En Route": "enRoute",
      Delivered: "delivered",
      Invoiced: "invoiced",
      Booked: "booked",
      Assigned: "assigned",
      "In Progress": "inProgress",
      Completed: "completed",
      // Also handle lowercase and variations
      new: "new",
      pending: "pending",
      "en route": "enRoute",
      enroute: "enRoute",
      delivered: "delivered",
      invoiced: "invoiced",
      booked: "booked",
      assigned: "assigned",
      "in progress": "inProgress",
      inprogress: "inProgress",
      completed: "completed",
      // Common variations
      in_progress: "inProgress",
      "in-progress": "inProgress",
      en_route: "enRoute",
      "en-route": "enRoute",
      scheduled: "pending",
      cancelled: "cancelled",
      delayed: "paused",
    };

    const mappedStatus =
      statusTitleToIdMappings[status] ||
      statusTitleToIdMappings[status?.toLowerCase()];

    if (mappedStatus && colorConfig.statusColors[mappedStatus]) {
      return colorConfig.statusColors[mappedStatus];
    }

    // Fallback to gray for unknown statuses
    return "#38bc8a";
  };

  /**
   * Get status color directly by status name
   * @param {string} status - Status name
   * @returns {string} Hex color code
   */
  const getStatusColor = (status) => {
    return colorConfig.statusColors[status] || "#38bc8a";
  };

  /**
   * Get all available status colors
   * @returns {Object} Object with status as key and color as value
   */
  const getAllStatusColors = () => {
    return colorConfig.statusColors;
  };

  /**
   * Get background color for a grid cell based on the time slot
   * @param {Object} timeSlot - TimeSlot object with isWorkHour and isWeekend properties
   * @returns {string} Hex color code
   */
  const getGridCellColor = (timeSlot) => {
    // Weekend background
    if (timeSlot.isWeekend) {
      return "#f8f9fa"; // Light gray for weekends
    }

    // Non-work hours background
    if (!timeSlot.isWorkHour) {
      return "#f5f5f5"; // Slightly darker gray for non-work hours
    }

    // Work hours background
    return "#ffffff"; // White for work hours
  };

  /**
   * Get color for an event based on its properties
   * @param {Object} event - Event object with status, priority, and type properties
   * @returns {string} Hex color code
   */
  const getEventColor = (event) => {
    // Use the same logic as getJobColor for consistency
    return getJobColor(event);
  };

  /**
   * Get border color for priority indication
   * @param {string} priority - Priority level (high, medium, low)
   * @returns {string|null} Hex color code or null if no priority
   */
  const getPriorityBorderColor = (priority) => {
    if (!priority) {
      return null;
    }

    const priorityColors = {
      high: "#dc2626", // Red
      medium: "#f59e0b", // Orange
      low: "#10b981", // Green
      urgent: "#dc2626", // Red (same as high)
    };

    return priorityColors[priority?.toLowerCase()] || null;
  };

  /**
   * Get border color for status indication
   * @param {string} status - Status name
   * @returns {string|null} Hex color code or null if no status
   */
  const getStatusBorderColor = (status) => {
    if (!status) {
      return null;
    }

    // Use the same color as status color but return null if not found
    const statusColor = getStatusColor(status);
    const borderColor = statusColor !== "#38bc8a" ? statusColor : null;

    return borderColor;
  };

  return {
    // Color configurations
    statusColors: colorConfig.statusColors,
    selectedTheme: colorConfig.selectedTheme,
    workHours,

    // Helper functions
    getJobColor,
    getStatusColor,
    getAllStatusColors,
    getGridCellColor,
    getEventColor,
    getPriorityBorderColor,
    getStatusBorderColor,
  };
};

export default useDispatchColors;
